import {
    ErrorOutlined,
    NotificationsOutlined,
    RefreshRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Divider,
    List,
    ListItem,
    Modal,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import { useUserService } from '../../services/user.service';
import { useTransactionService, DetailsType } from '../../services/transaction.service';
import { useIgnoredIssues } from '../smart-check/hooks/useIgnoredIssues';
import { QUERY_KEYS } from '../../utils/queryKeys';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';


const StatusIndicator: React.FC = () => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { inProgressIntegrations } = useIntegrationsStatusContext();
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const userService = useUserService();
    const transactionService = useTransactionService();
    const queryClient = useQueryClient();
    const iconSize = isMobile ? '1.25rem' : '1.5rem';

    // Invalidate integrations query when component mounts
    useEffect(() => {
        if (currentCtUser) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
        }
    }, [queryClient, currentCtUser]);


    const { data: notifications } = useQuery({
        queryKey: [QUERY_KEYS.USER_NOTIFICATIONS],
        queryFn: () => {
            const notifications = userService.getNotifications(20);
            userService.notificationsMarkAsRead();
            return notifications;
        },
        enabled: !!currentCtUser,
    });

    // Query for Smart Check issues
    const { data: issuesData } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUES],
        queryFn: () => transactionService.getAllTimeIssueCount(DetailsType.ONLY_ISSUES),
        enabled: !!currentCtUser,
    });

    // Query for ignored issues data
    const { data: allIgnoredIssues } = useIgnoredIssues();

    // Use the unified account state hook
    const statusInfo = useUnifiedAccountState({
        issuesData,
        ignoredIssues: allIgnoredIssues,
        iconSize
    });

    // Handle click based on status type
    const handleStatusClick = () => {
        // Navigate to the target if one is defined, otherwise open modal
        if (statusInfo.navigationTarget) {
            navigate(statusInfo.navigationTarget);
        } else {
            setStatusModalOpen(true);
        }
    };

    return (
        <>
            <Button
                onClick={handleStatusClick}
                startIcon={statusInfo.icon}
                sx={{
                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1, sm: 1.25 },
                    minWidth: 'auto',
                    borderRadius: 2,
                    textTransform: 'none',
                    bgcolor: (theme) => theme.palette.mode === 'dark' 
                        ? `${statusInfo.color}20`
                        : `${statusInfo.color}10`,
                    border: `1px solid ${statusInfo.color}40`,
                    color: statusInfo.color,
                    boxShadow: `0 1px 4px ${statusInfo.color}20`,
                    transition: 'all 0.2s ease-in-out',
                    '& .MuiButton-startIcon': {
                        color: statusInfo.color,
                        marginRight: { xs: 0.75, sm: 1 },
                        '& svg': {
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
                        }
                    },
                    '&:hover': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' 
                            ? `${statusInfo.color}30`
                            : `${statusInfo.color}20`,
                        transform: 'translateY(-0.5px)',
                        boxShadow: `0 2px 6px ${statusInfo.color}30`,
                    },
                }}
            >
                {statusInfo.status}
            </Button>

            <Modal
                open={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                aria-labelledby="status-modal-title"
            >
                <Paper
    sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90vw', sm: 380, md: 480 },
        maxWidth: '95vw',
        p: { xs: 2, sm: 3 },
        borderRadius: { xs: 2, sm: 2, md: 3 },
        boxShadow: theme.shadows[10],
        outline: 'none',
        }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                            sx={{
                                mr: 1.5,
                                color: statusInfo.color,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {statusInfo.icon}
                        </Box>
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            component="h2"
                            id="status-modal-title"
                            sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}
                        >
                            {statusInfo.details}
                        </Typography>
                    </Box>
                    {inProgressIntegrations.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <List dense disablePadding>
                                {inProgressIntegrations.map((integration, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                        <RefreshRounded 
                                            fontSize="small" 
                                            sx={{ 
                                                mr: 1, 
                                                color: theme.palette.info.main,
                                                animation: 'spin 2s linear infinite',
                                                '@keyframes spin': {
                                                    '0%': {
                                                        transform: 'rotate(0deg)',
                                                    },
                                                    '100%': {
                                                        transform: 'rotate(360deg)',
                                                    },
                                                },
                                            }} 
                                        />
                                        <Typography variant="body2">{integration}</Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {notifications && notifications.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ my: 1 }} />
                            <List dense disablePadding>
                                {notifications.map((notification, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                        {notification.amount === -1 ? (
                                            <>
                                                <ErrorOutlined
                                                    fontSize="small"
                                                    sx={{ mr: 1, color: theme.palette.error.main }}
                                                />
                                                <Typography variant="body2" color="error.main">
                                                    {`${notification.name}-Import fehlgeschlagen`}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <NotificationsOutlined
                                                    fontSize="small"
                                                    sx={{ mr: 1, color: theme.palette.info.main }}
                                                />
                                                <Typography variant="body2">
                                                    {`${notification.amount} neue ${notification.name}-Transaktionen wurden importiert`}
                                                </Typography>
                                            </>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Paper>
            </Modal>
        </>
    );
};

export default StatusIndicator;
